import { MAIL_CURRENCY_KEYS } from '@rb/shared/mail/reward-validation';

export type RewardOptionGroup = 'currency' | 'special' | 'pet' | 'character' | 'skin';

export interface RewardOption {
  group: RewardOptionGroup;
  key: string; // delivery key: GOLD / PET_SLOT / AD_REMOVE / PET_5 / CHARACTER_1 / SKIN_1_1001
  label: string;
  quantifiable: boolean; // 개수 입력 활성 여부
}

/**
 * 보상 피커 소스 행. R2 의 readable client 컬렉션(`clientPets`/`clientCharacters`)
 * 형태를 그대로 받는다. `name` 은 l10n 키(예: `PET_1_NAME`)이므로 호출부가
 * `resolveName` 으로 실제 이름으로 풀어 라벨에 쓴다.
 */
export interface PetSource {
  id: number;
  name?: string;
}
export interface SkinSource {
  id: number;
  name?: string;
}
export interface CharacterSource {
  id: number;
  name?: string;
  skins?: SkinSource[];
}

const identityName = (key?: string): string => key ?? '';

/**
 * 발송 가능 보상 옵션 목록을 구성.
 *
 * @param pets        R2 clientPets 행 (id + l10n-key name)
 * @param characters  R2 clientCharacters 행 (id + l10n-key name + skins[])
 * @param resolveName l10n 키 → 표시 이름 해석기 (기본: 키 그대로). miss/빈 값이면
 *                    라벨은 id 만으로 fallback.
 */
export function buildRewardOptions(
  pets: PetSource[],
  characters: CharacterSource[],
  resolveName: (key?: string) => string = identityName
): RewardOption[] {
  const out: RewardOption[] = [];
  for (const k of MAIL_CURRENCY_KEYS) {
    out.push({ group: 'currency', key: k, label: k, quantifiable: true });
  }
  out.push({ group: 'special', key: 'PET_SLOT', label: 'Pet Slot (+N)', quantifiable: true });
  out.push({ group: 'special', key: 'AD_REMOVE', label: 'Remove Ads', quantifiable: false });

  for (const p of pets ?? []) {
    const name = resolveName(p.name).trim();
    out.push({
      group: 'pet',
      key: `PET_${p.id}`,
      label: name ? `Pet #${p.id} ${name}` : `Pet #${p.id}`,
      quantifiable: false,
    });
  }

  for (const c of characters ?? []) {
    const charName = resolveName(c.name).trim();
    out.push({
      group: 'character',
      key: `CHARACTER_${c.id}`,
      label: charName ? `Character #${c.id} ${charName}` : `Character #${c.id}`,
      quantifiable: false,
    });
    for (const s of c.skins ?? []) {
      const skinName = resolveName(s.name).trim();
      const charLabel = charName || `#${c.id}`;
      const skinLabel = skinName || `#${s.id}`;
      out.push({
        group: 'skin',
        key: `SKIN_${c.id}_${s.id}`,
        label: `Skin ${charLabel} / ${skinLabel}`,
        quantifiable: false,
      });
    }
  }
  return out;
}
